import React from "react";
import PropTypes from "prop-types";
import { Bell, Menu } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { UserProfileDropdown } from "./user-profile-dropdown";
import { useMobileDetection } from "../../hooks/useMobileDetection";

/**
 * ShellHeaderV3 - the single "welcome" header for list/dashboard pages.
 *
 * Mobile: hamburger (left) | logo (center) | bell (right), plus a fixed
 * bottom action bar with Quote / Start New Application buttons.
 * Desktop: title/subtitle on the left, actions + bell + avatar on the right.
 */
export function ShellHeaderV3({
  title = "Welcome Back",
  subtitle,
  mobileLogo,
  actions = {},
  user = {},
  onNotificationClick,
  notificationCount = 0,
  onMenuClick,
  showMenuButton = true,
  hideHamburgerMenu = false,
  className = "",
}) {
  const {
    showGetQuote = true,
    onGetQuote,
    getQuoteLabel = "Get Quote",
    showStartNewApplication = true,
    onStartNewApplication,
    startNewApplicationLabel = "Start New Application",
    disableStartNewApplication = false,
  } = actions;

  const hasMobileActions = showGetQuote || showStartNewApplication;
  const { isMobile } = useMobileDetection();

  return (
    <header className={cn("flex-shrink-0", className)}>
      {isMobile ? (
        <>
          {/* ── Mobile top bar: hamburger | centered logo | bell ── */}
          <div className="flex items-center justify-between px-5 h-[72px] bg-neutral-1 border-b border-neutral-5">
            {showMenuButton && !hideHamburgerMenu ? (
              <button
                type="button"
                onClick={onMenuClick}
                className="p-1 rounded-md hover:bg-neutral-2 transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-8 h-8 text-neutral-12" />
              </button>
            ) : (
              <div className="w-10 h-10" />
            )}

            <div className="flex items-center justify-center">{mobileLogo}</div>

            <button
              type="button"
              onClick={onNotificationClick}
              className="relative p-1 rounded-md hover:bg-neutral-2 transition-colors"
              aria-label="Notifications"
            >
              <Bell className="w-7 h-7 text-neutral-10" />
              {notificationCount > 0 && (
                <span className="absolute top-1 right-1 grid place-items-center rounded-full bg-red-500 text-white font-medium min-w-[14px] h-[14px] px-1 text-[10px]">
                  {notificationCount > 99 ? "99+" : notificationCount}
                </span>
              )}
            </button>
          </div>

          {/* ── Mobile actions sub-header (flows directly below the top bar) ── */}
          {hasMobileActions && (
            <div className="flex items-center gap-3 px-5 py-3 bg-neutral-1 border-b border-[#e8e2d8]">
              {showGetQuote && (
                <Button
                  variant="outline"
                  className="border-secondary-2 text-secondary-2 bg-transparent shrink-0 w-[130px] rounded-lg text-xs"
                  onClick={onGetQuote}
                >
                  {getQuoteLabel}
                </Button>
              )}
              {showStartNewApplication && (
                <Button
                  variant="default"
                  className="bg-secondary-2 text-white flex-1 rounded-lg text-xs"
                  onClick={onStartNewApplication}
                  disabled={disableStartNewApplication}
                >
                  {startNewApplicationLabel}
                </Button>
              )}
            </div>
          )}
        </>
      ) : (
        /* ── Desktop bar: title/subtitle | actions + bell + avatar ── */
        <div className="flex items-center justify-between gap-4 px-12 h-[84px] bg-neutral-1 border-b border-neutral-5">
          <div className="min-w-0 gap-2 flex flex-col">
            <h1 className="text-2xl font-bold text-neutral-12 truncate">{title}</h1>
            {subtitle && (
              <p className="text-sm text-neutral-8 truncate">{subtitle}</p>
            )}
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              {showGetQuote && (
                <Button
                  variant="outline"
                  size="default"
                  className="border-secondary-2 text-secondary-2 bg-transparent hover:shadow-md"
                  onClick={onGetQuote}
                >
                  {getQuoteLabel}
                </Button>
              )}
              {showStartNewApplication && (
                <Button
                  variant="default"
                  size="default"
                  className="bg-secondary-2 text-white hover:shadow-md"
                  onClick={onStartNewApplication}
                  disabled={disableStartNewApplication}
                >
                  {startNewApplicationLabel}
                </Button>
              )}
            </div>

            <button
              type="button"
              onClick={onNotificationClick}
              className="relative p-2 rounded-md hover:bg-neutral-2 transition-colors"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5 text-neutral-10" />
              {notificationCount > 0 && (
                <span className="absolute top-1 right-1 grid place-items-center rounded-full bg-red-500 text-white font-medium min-w-[14px] h-[14px] px-1 text-[10px]">
                  {notificationCount > 99 ? "99+" : notificationCount}
                </span>
              )}
            </button>

            {user?.showProfile !== false && (
              <UserProfileDropdown
                userInitials={user.initials}
                userImage={user.image}
                userName={user.name}
                onProfileClick={user.onProfileClick}
                onLogoutClick={user.onLogout}
              />
            )}
          </div>
        </div>
      )}
    </header>
  );
}

ShellHeaderV3.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  mobileLogo: PropTypes.node,
  actions: PropTypes.shape({
    showGetQuote: PropTypes.bool,
    onGetQuote: PropTypes.func,
    getQuoteLabel: PropTypes.string,
    showStartNewApplication: PropTypes.bool,
    onStartNewApplication: PropTypes.func,
    startNewApplicationLabel: PropTypes.string,
    disableStartNewApplication: PropTypes.bool,
  }),
  user: PropTypes.shape({
    initials: PropTypes.string,
    image: PropTypes.string,
    name: PropTypes.string,
    showProfile: PropTypes.bool,
    onProfileClick: PropTypes.func,
    onLogout: PropTypes.func,
  }),
  onNotificationClick: PropTypes.func,
  notificationCount: PropTypes.number,
  onMenuClick: PropTypes.func,
  showMenuButton: PropTypes.bool,
  hideHamburgerMenu: PropTypes.bool,
  className: PropTypes.string,
};
